import { useEffect, useState } from 'react';

/* ── Anneau de score segmenté (mode pastel) ───────────────────────────
   La signature graphique de la DA cousine de Visaly : quatre arcs pastel
   séparés par des respirations, qui se remplissent au fur et à mesure du
   score. Les arcs non atteints restent en version pâle — le nuancier
   complet reste à l'écran quel que soit le résultat.

   Les couleurs viennent des variables du nuancier actif (`--p-a`…`--p-d`,
   cf. client/index.css) : changer de nuancier recolore l'anneau sans
   toucher au code. Ces composants ne sont rendus QU'EN mode pastel. */

const SEGMENTS = ['a', 'b', 'c', 'd'].map((s) => ({
  fill: `var(--p-${s}-sig)`,
  pale: `var(--p-${s}-pale)`,
}));

const R = 78;
const CIRC = 2 * Math.PI * R;
const GAP = 16;
const SEG_SPAN = CIRC / SEGMENTS.length;
const SEG_LEN = SEG_SPAN - GAP;

interface Props {
  /** Score 0–100. */
  score: number;
  /** Diamètre rendu en px. */
  size?: number;
  /** Contenu central. Par défaut : le score + « /100 ». */
  children?: React.ReactNode;
  /** Épaisseur du tracé (unités du viewBox 200×200). */
  thickness?: number;
  className?: string;
}

export default function PastelScoreRing({
  score,
  size = 176,
  thickness = 15,
  children,
  className = '',
}: Props) {
  // Remplissage animé au montage (0 → score).
  const [drawn, setDrawn] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(Math.max(0, Math.min(100, score))));
    return () => cancelAnimationFrame(id);
  }, [score]);

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 200 200" className="-rotate-90">
        {SEGMENTS.map((seg, i) => {
          const start = i * SEG_SPAN + GAP / 2;
          // Part du segment couverte par le score (0 → 1).
          const ratio = Math.max(0, Math.min(1, (drawn - i * 25) / 25));
          return (
            <g key={i}>
              {/* Piste pâle — toujours visible, c'est elle qui porte le nuancier */}
              <circle
                cx="100" cy="100" r={R} fill="none" strokeLinecap="round"
                stroke={seg.pale} strokeWidth={thickness}
                strokeDasharray={`${SEG_LEN} ${CIRC - SEG_LEN}`}
                strokeDashoffset={-start}
              />
              {/* Arc rempli */}
              <circle
                cx="100" cy="100" r={R} fill="none" strokeLinecap="round"
                stroke={seg.fill} strokeWidth={thickness}
                strokeDasharray={`${SEG_LEN * ratio} ${CIRC - SEG_LEN * ratio}`}
                strokeDashoffset={-start}
                style={{
                  transition: `stroke-dasharray 900ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 110}ms`,
                }}
              />
            </g>
          );
        })}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children ?? (
          <>
            <span className="font-display text-slate-900 leading-none" style={{ fontSize: size * 0.28 }}>
              {score}
            </span>
            <span className="text-slate-400 mt-1" style={{ fontSize: size * 0.075 }}>
              /100
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Variante indéterminée compacte ─────────────────────────────────
   Pour les états d'attente (chargement de session, écran de connexion) :
   les quatre arcs pastel tournent lentement. */
export function PastelPulseRing({ size = 88, thickness = 13, className = '' }: { size?: number; thickness?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" className={`animate-spin-slow ${className}`}>
      {SEGMENTS.map((seg, i) => {
        const start = i * SEG_SPAN + GAP / 2;
        return (
          <circle
            key={i}
            cx="100" cy="100" r={R} fill="none" strokeLinecap="round"
            stroke={seg.fill} strokeWidth={thickness}
            strokeDasharray={`${SEG_LEN} ${CIRC - SEG_LEN}`}
            strokeDashoffset={-start}
            opacity={0.45 + i * 0.18}
          />
        );
      })}
    </svg>
  );
}
